import ProfileIdentity from './ProfileIdentity';
import ProfileStats from './ProfileStats';
import {
  profilePath,
  profileSection,
  RAIL_SECTIONS,
  railSectionKey,
} from './sections';
import { Box, NavigationList, Stack } from '@presov.sk/presov-ui-react';
import React from 'react';
import { useLocation } from 'react-router';

/** Šírka pásu podľa dizajnového balíka; shell má svoju bočnú lištu 236 px. */
export const PROFILE_RAIL_WIDTH = 300;

type Props = {
  summary: React.ComponentProps<typeof ProfileStats>['summary'];
};

/**
 * Ľavý pás profilu: kto som, ako na tom som a päť sekcií (História notifikácií je tab v Nastaveniach).
 *
 * Prepínač sekcií je `NavigationList` z knižnice, nie vlastné tlačidlá - je to
 * tá istá navigácia ako v shelle, len nad podstránkami jednej obrazovky, takže
 * má mať aj ten istý aktívny stav a tú istú ikonu. **Počty v riadkoch
 * navigácie z dizajnového balíka tu nie sú**: `NavigationList` na ne nemá slot
 * a boli by to tie isté tri čísla, ktoré stoja v súhrne o dva centimetre
 * vyššie. Keby sa raz doplnili, patria do knižnice ako pole `NavItem`-u, nie
 * sem ako druhá navigácia.
 *
 * Pás sa kreslí **len na desktope**. Na mobile je z neho karta so súhrnom
 * a vodorovné záložky - 300 px vedľa obsahu tam nie je kde vziať.
 */
const ProfileRail: React.FC<Props> = ({ summary }) => {
  const { pathname } = useLocation();

  // Sekcia, ktorá je tabom inej (História v Nastaveniach), rozsvieti rodiča.
  const current = profileSection(pathname.split('/')[2]);
  const currentPath = current ? profilePath(railSectionKey(current)) : pathname;

  return (
    <Stack
      sx={{
        flex: 'none',
        width: PROFILE_RAIL_WIDTH,
        minHeight: 0,
        overflowY: 'auto',
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack sx={{ flex: 'none', px: 2.5, pt: 3 }}>
        <ProfileIdentity summary={summary} />
        <Box sx={{ mt: 2 }}>
          <ProfileStats summary={summary} />
        </Box>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, px: 1.5, pb: 2 }}>
        <NavigationList
          items={RAIL_SECTIONS.map((section) => ({
            href: profilePath(section.key),
            label: section.label,
            icon: section.icon,
          }))}
          currentPath={currentPath}
        />
      </Box>
    </Stack>
  );
};

export default ProfileRail;
